import i18n from 'i18next';
import React from 'react';
import moment from 'moment-timezone';
import { Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Styles from '../styles/event/dashboard/eventDashboard.module.css';
import TransfersStatusModel from '../models/transferStatusModel';
import ModalSettingsEvent from '../pages/rates/components/modalSettingsEvent.js';

function MissingDocumentsWarning({ eventId = null, documents }) {
    const { t } = useTranslation();
    const getEventId = title => {
        let found = title.match(/\(E(\d*)\)/);
        if (found) {
            return found[1];
        }
    };
    const [currentEventDocuments, setCurrentEventDocuments] = React.useState([]);

    const otherEventsDocuments = React.useMemo(() => {
        let _documents = { ...documents };
        for (const [key, value] of Object.entries(documents)) {
            let documentEventId = getEventId(key);
            if (eventId && +documentEventId === eventId) {
                setCurrentEventDocuments(value);
                delete _documents[key];
                break;
            }
        }
        return _documents;
    }, [documents]);

    if (Object.keys(documents).length < 1) {
        return null;
    }

    return (
        <>
            {currentEventDocuments.length > 0 && (
                <>
                    <p className={Styles['event-documents-text']}>{t('ticket.eventDashboard.messages.vouchers-missing.content.see-more.list-title')}</p>
                    <ul className={Styles['event-documents-list']}>
                        {currentEventDocuments.map((_document, index) => (
                            <li key={index}>{_document}</li>
                        ))}
                    </ul>
                </>
            )}
            {Object.keys(otherEventsDocuments).length > 0 && (
                <>
                    <p className={Styles['event-documents-text']}>{t('ticket.eventDashboard.messages.vouchers-missing.content.see-more.other-events')}</p>
                    {Object.entries(otherEventsDocuments).map(event => (
                        <>
                            <p className={Styles['event-title']}>{event[0]}</p>
                            <ul className={Styles['event-documents-list']}>
                                {event[1].map(_document => (
                                    <li key={_document}>{_document}</li>
                                ))}
                            </ul>
                        </>
                    ))}
                </>
            )}
        </>
    );
}

export default function WarningMessage({ organization, event = null, settings = null }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [showSalesEndSessionsModal, setShowSalesEndSessionsModal] = React.useState(null);

    const [status, statusLoading] = TransfersStatusModel.useApiModel(
        {
            organizationId: organization.id,
            headers: {
                'Accept-Language': i18n.language
            }
        },
        [organization]
    );

    const closeEvent = React.useCallback(() => {
        if (event) {
            event.publication_status = 'closed';
            event
                .save({
                    organizationId: organization.id
                })
                .then(() => {
                    toast.success(t('ticket.eventDashboard.messages.event-terminated.toast_success'));
                })
                .catch(() => {
                    toast.error(t('ticket.eventDashboard.messages.event-terminated.toast_error'));
                });
        }
    }, [event, organization, toast]);

    if (statusLoading || !status || status.status) {
        return null;
    }

    return (
        <>
            <MissingDocuments event={event} status={status} />

            {!organization.isBrandTicketPro() && (status.organisateur_info.status || status.missing_bankdata.status) && (
                <Message title={t('ticket.eventDashboard.messages.informations-missing.title')} type="danger">
                    <p style={{ marginBottom: 0 }}>{t('ticket.eventDashboard.messages.informations-missing.content.description')}</p>
                    <div className="wz-message-links">
                        {status.organisateur_info.status && (
                            <a
                                className="wz-message-link"
                                href={`/ticket/O${organization.id}/organization/profile`}
                                onClick={e => {
                                    e.preventDefault();
                                    history.push(`/ticket/O${organization.id}/organization/profile`);
                                }}
                            >
                                {t('ticket.eventDashboard.messages.informations-missing.content.link-1')}
                            </a>
                        )}
                        {status.missing_bankdata.status && (
                            <a
                                className="wz-message-link"
                                href={`/ticket/O${organization.id}/organization/bank`}
                                onClick={e => {
                                    e.preventDefault();
                                    history.push(`/ticket/O${organization.id}/organization/bank`);
                                }}
                            >
                                {t('ticket.eventDashboard.messages.informations-missing.content.link-2')}
                            </a>
                        )}
                    </div>
                </Message>
            )}
            {event && status.end_of_event.status && event.publication_status !== 'closed' && event.end_date !== null && moment(event.end_date) < moment() && (
                <Message
                    title={t('ticket.eventDashboard.messages.event-terminated.title')}
                    type="default"
                    links={{
                        label: t('ticket.eventDashboard.messages.event-terminated.content.cta'),
                        onClick: closeEvent
                    }}
                >
                    <p>{t('ticket.eventDashboard.messages.event-terminated.content.message-1')}</p>
                    <p>{t('ticket.eventDashboard.messages.event-terminated.content.message-2')}</p>
                </Message>
            )}

            {event && settings?.start_sale && (
                <Message
                    title={t('ticket.eventDashboard.messages.for-sale-programming.title')}
                    type="default"
                    links={{
                        label: t('ticket.eventDashboard.messages.for-sale-programming.content.cta'),
                        link: '#',
                        onClick: () => {
                            setShowSalesEndSessionsModal(true);
                        }
                    }}
                >
                    <ModalSettingsEvent
                        organization={organization}
                        event={event}
                        settings={settings}
                        showModal={showSalesEndSessionsModal}
                        onSuccess={() => {
                            setShowSalesEndSessionsModal(false);
                        }}
                        onCloseModal={() => {
                            setShowSalesEndSessionsModal(false);
                        }}
                    ></ModalSettingsEvent>
                </Message>
            )}
        </>
    );
}

function MissingDocuments({ event, status }) {
    const { t } = useTranslation();

    if (!status.mod_warning.message?.details || Array.isArray(status.mod_warning.message.details)) {
        return null;
    }
    return (
        <Message
            title={t('ticket.eventDashboard.messages.vouchers-missing.title')}
            type="warning"
            seeMore={<MissingDocumentsWarning eventId={event?.id} documents={status.mod_warning.message.details} />}
            seeMoreLabel={t('ticket.common.seeMore')}
            seeLessLabel={t('ticket.common.seeLess')}
        >
            <p>{t('ticket.eventDashboard.messages.vouchers-missing.content.message-1')}</p>
            <p style={{ marginBottom: 0 }}>{t('ticket.eventDashboard.messages.vouchers-missing.content.message-2')}</p>
            <p>{t('ticket.eventDashboard.messages.vouchers-missing.content.message-3')}</p>
        </Message>
    );
}
